/**
 * MissingFieldsAlert — small amber pill that surfaces incomplete data on
 * a record. Pass a list of `{ label, present }` entries; the component
 * renders nothing when every field is present, otherwise shows:
 *
 *   ⚠ 3 missing fields  (hover/click for list)
 *
 * Used on Lead, Proposal, Project, Contract detail panels to remind
 * users which key fields still need to be filled out — non-blocking.
 */
import { AlertTriangle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export function MissingFieldsAlert({ fields, testId = 'missing-fields-alert', label }) {
  const missing = (fields || []).filter((f) => !f.present);
  if (missing.length === 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-400 text-xs font-medium hover:bg-amber-500/20 transition-colors"
          data-testid={testId}
        >
          <AlertTriangle className="h-3 w-3" />
          {missing.length} missing {missing.length === 1 ? 'field' : 'fields'}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 bg-popover border-border" align="start">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
          {label || 'Recommended fields'}
        </p>
        <ul className="space-y-1">
          {missing.map((f) => (
            <li key={f.label} className="text-sm flex items-center gap-1.5">
              <span className="h-1 w-1 rounded-full bg-amber-400" />
              {f.label}
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground mt-2 pt-2 border-t border-white/10">
          These help keep records complete but won&apos;t block saving.
        </p>
      </PopoverContent>
    </Popover>
  );
}
